import { OrgCapability, hasOrgCapability } from "../../shared/capabilities";

import {
  courseGroupingSelectors,
  courseGroupingUserSelectors,
  courseSelectors,
  courseUserSelectors,
  createAppSelector,
  createStringArraySelector,
  sectionSelectors,
} from "./base";
import { selectCurrentUserOrgRole } from "./permissions";

import { compactMap, filterMap } from "utils/collections";

/** Courses visible in the admin course UI (CourseUser / CourseGroupingUser scoped; org authors see all). */
export const selectAdminAccessibleCourses = createAppSelector(
  [
    courseSelectors.selectAll,
    selectCurrentUserOrgRole,
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
  ],
  (courses, orgUserRole, currentUserCourseUsers, currentUserGroupingUsers) => {
    if (hasOrgCapability(orgUserRole, OrgCapability.CAN_AUTHOR_ORG)) {
      return courses;
    }

    const accessibleCourseIds = new Set(currentUserCourseUsers.map((cu) => cu.course_id));
    const accessibleGroupingIds = new Set(
      currentUserGroupingUsers.map((gu) => gu.course_grouping_id),
    );
    return courses.filter(
      (course) =>
        accessibleCourseIds.has(course.id) ||
        (course.grouping_id != null && accessibleGroupingIds.has(course.grouping_id)),
    );
  },
);

/** Course ids whose grouping_id is not `groupingId` (includes ungrouped). */
export const selectCourseIdsExceptGroupingId = createStringArraySelector(
  [(state, groupingId: string) => groupingId, courseSelectors.selectAll],
  (groupingId, courses): string[] =>
    compactMap(courses, (course) => (course.grouping_id !== groupingId ? course.id : null)),
);

/** Accessible course ids that have ≥1 section in the term, in code order. */
export const selectCourseIdsWithSectionsByTerm = createStringArraySelector(
  [
    (state, termId: string | null | undefined) => termId ?? null,
    selectAdminAccessibleCourses,
    sectionSelectors.selectAll,
  ],
  (termId, courses, sections): string[] => {
    if (!termId) {
      return [];
    }

    const courseIdsWithSections = new Set(
      compactMap(sections, (section) => (section.term_id === termId ? section.course_id : null)),
    );
    return compactMap(courses, (course) =>
      courseIdsWithSections.has(course.id) ? course.id : null,
    );
  },
);

/** Grouping ids that have courses with sections in the term (title order). */
export const selectGroupingIdsWithCoursesByTerm = createStringArraySelector(
  [
    selectCourseIdsWithSectionsByTerm,
    courseSelectors.selectEntities,
    courseGroupingSelectors.selectIds,
  ],
  (courseIds, coursesById, groupingIds): string[] => {
    const groupingIdsWithCourses = new Set<string>();
    for (const courseId of courseIds) {
      const groupingId = coursesById[courseId]?.grouping_id;
      if (groupingId) {
        groupingIdsWithCourses.add(groupingId);
      }
    }
    return groupingIds.filter((id) => groupingIdsWithCourses.has(id));
  },
);

/** Course ids with sections in the term for a grouping (`null` = ungrouped), in code order. */
export const selectCourseIdsByGroupingIdAndTerm = createStringArraySelector(
  [
    selectCourseIdsWithSectionsByTerm,
    (state, termId: string | null | undefined, groupingId: string | null) => groupingId,
    courseSelectors.selectEntities,
  ],
  (courseIds, groupingId, coursesById): string[] =>
    courseIds.filter((courseId) => coursesById[courseId]?.grouping_id === groupingId),
);

/** Grouping ids for the admin course list (title order). Empty groupings included for org authors. */
export const selectCourseGroupingIdsWithCourses = createStringArraySelector(
  [courseGroupingSelectors.selectIds, selectAdminAccessibleCourses, selectCurrentUserOrgRole],
  (groupingIds, courses, orgRole): string[] => {
    if (hasOrgCapability(orgRole, OrgCapability.CAN_AUTHOR_ORG)) {
      return groupingIds;
    }

    const accessibleGroupingIds = new Set<string>();
    for (const course of courses) {
      if (course.grouping_id) {
        accessibleGroupingIds.add(course.grouping_id);
      }
    }
    return groupingIds.filter((id) => accessibleGroupingIds.has(id));
  },
);

/** Ungrouped accessible course ids, in code order. */
export const selectUngroupedCourseIds = createStringArraySelector(
  [selectAdminAccessibleCourses],
  (courses): string[] => compactMap(courses, (course) => (!course.grouping_id ? course.id : null)),
);

/** Course ids for a grouping, in code order. */
export const selectCourseIdsByGroupingId = createStringArraySelector(
  [(state, groupingId: string | null) => groupingId, courseSelectors.selectAll],
  (groupingId, courses): string[] =>
    filterMap(
      courses,
      (course) => course.grouping_id === groupingId,
      (course) => course.id,
    ),
);

/** Admin-accessible course ids for a grouping, in code order. */
export const selectAdminAccessibleCourseIdsByGroupingId = createStringArraySelector(
  [(state, groupingId: string | null) => groupingId, selectAdminAccessibleCourses],
  (groupingId, courses): string[] =>
    filterMap(
      courses,
      (course) => course.grouping_id === groupingId,
      (course) => course.id,
    ),
);
